import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Lock from '@material-ui/icons/Lock';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Typography from '@material-ui/core/Typography';
import './LoginForm.scss';

const focusUsernameInputField = (input) => {
  if (input) {
    setTimeout(() => {
      input.focus();
    }, 100);
  }
};

const LoginForm = (props) => {
  const { onSubmit, from, admin, loginButtonText, title, focus, active } = props;
  const [username, setUsername] = useState(() => props.username);
  const [password, setPassword] = useState('');
  const [{ error, processing, redirectToReferrer }, setPageState] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    setPageState({ error: '', processing: true });
    onSubmit({ username, password, admin })
    .then(() => {
      setPageState({ processing: false, redirectToReferrer: true });
    })
    .catch((err) => {
      if (err.response && err.response.data && err.response.data.error) {
        setPageState({ error: err.response.data.error.message, processing: false });
      } else {
        setPageState({ error: err, processing: false });
      }
    });
  };

  const handleUsernameChange = useCallback((e) => {
    setUsername(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  if (redirectToReferrer) {
    return <Redirect to={from} />;
  }

  return (
    <div className={`grb-login-form${active ? ' grb-login-form-active' : ' grb-login-form-inactive'}`}>
      <div className="grb-login-page-content">
        <Avatar>
          <LockOutlinedIcon />
        </Avatar>
        <Typography variant="h5">
          {title}
        </Typography>
        {error && (
          <div className="grb-login-page-error">
            <Typography variant="body1" id={admin ? 'grb-admin-logging-error' : 'grb-logging-error'}>
              {error}
            </Typography>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <FormControl margin="normal" required fullWidth>
            <InputLabel htmlFor="username">Username</InputLabel>
            <Input
              id={admin ? 'adminUsername' : 'username'}
              name={admin ? 'adminUsername' : 'username'}
              autoComplete={admin ? 'adminUsername' : 'username'}
              autoFocus
              inputRef={focus ? focusUsernameInputField : undefined}
              value={username || ''}
              onChange={handleUsernameChange}
              disabled={processing}
              startAdornment={(
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              )}
            />
          </FormControl>
          <FormControl margin="normal" required fullWidth>
            <InputLabel htmlFor="password">Password</InputLabel>
            <Input
              type="password"
              id={admin ? 'adminPassword' : 'password'}
              name={admin ? 'adminPassword' : 'password'}
              autoComplete={admin ? 'admin-current-password' : 'current-password'}
              value={password || ''}
              onChange={handlePasswordChange}
              disabled={processing}
              startAdornment={(
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              )}
            />
          </FormControl>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className="grb-login-page-login-button"
            disabled={processing}
            id={admin ? 'submitAdminLogin' : 'submitLogin'}
          >
            {loginButtonText}
          </Button>
        </form>
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  username: PropTypes.string,
  from: PropTypes.shape({}).isRequired,
  title: PropTypes.string,
  loginButtonText: PropTypes.string,
  admin: PropTypes.bool,
  focus: PropTypes.bool,
  active: PropTypes.bool,
};

LoginForm.defaultProps = {
  username: undefined,
  title: 'Sign in',
  loginButtonText: 'Sign in',
  admin: undefined,
  focus: true,
  active: true,
};

export default LoginForm;

